<template>
  <div>
    <div class="flex items-center justify-start">
      <DebouncedButton
        :id="`performance-${performance.id}`"
        class="performance-button py-2 px-4 font-semibold text-sm font-button bg-button-guest-primary text-button-label disabled:cursor-not-allowed disabled:bg-disabled disabled:text-white min-w-[150px] max-w-[200px] truncate uppercase text-center"
        :disabled="disabled"
        :class="[buttonClasses, !isOnSale ? 'rounded-l' : 'rounded']"
        @click="onClick"
        :aria-label="`Book tickets for ${settingStore.timeStr(performance.datetime)} show – ${performance.show.name}`"
      >
        <span v-if="performance.sold">(SOLD OUT)</span>
        {{ buttonText }}
      </DebouncedButton>
      <DebouncedButton
        v-if="!isOnSale"
        @click="openModal"
        :id="`performance-${performance.id}-invite-code`"
        :class="unlockButtonClasses"
        class="performance-invite-code-button bg-disabled text-white rounded-r px-3 h-[36px] border-l-2"
        :aria-label="`Show is invite only. Unlock tickets for ${settingStore.timeStr(performance.datetime)} show – ${performance.show.name}`"
      >
        <LockClosedIcon class="w-4 h-4 stroke-[2]" />
      </DebouncedButton>
    </div>
    <p
      v-if="!isOnSale && performance.on_sale_datetime"
      id="on-sale-message"
      class="text-sm text-fg-disclaimer flex flex-col mt-1 -space-y-0.5"
    >
      <span>Goes on sale</span>
      <span>
        {{ settingStore.fullDate(performance.on_sale_datetime) }} @
        {{ settingStore.timeStr(performance.on_sale_datetime) }}
      </span>
    </p>
    <p
      v-if="hasPurchaseStop"
      id="off-sale-message"
      class="text-sm text-fg-disclaimer flex flex-col mt-1 -space-y-0.5"
    >
      No longer on sale
    </p>

    <!--Invite code validation process-->
    <SlotModal :display="displayCodeModal" @close="closeModal">
      <template #header>
        <h3 class="!text-2xl">Enter an invite code to unlock tickets.</h3>
      </template>
      <p class="mt-4 mb-2 max-w-xs">{{ performance.show.name }} {{ timeStr }}</p>
      <TextInputGuest id="invite-code-input" v-model.trim="inviteCode" placeholder="Invite Code" />
      <p
        v-if="errorOnVerifyingCode.message"
        id="invite-code-error-message"
        class="text-red-500 max-w-sm mt-2"
      >
        {{ errorOnVerifyingCode.message }}
      </p>
      <div class="flex items-center justify-end gap-4 mt-3">
        <DebouncedButton
          id="close-modal-button"
          class="py-2 px-4 rounded font-semibold text-sm font-button border border-button-guest-primary text-button-guest-primary disabled:opacity-60 disabled:cursor-not-allowed"
          @click="closeModal"
          :disabled="isVerifyingCode"
        >
          Close
        </DebouncedButton>
        <DebouncedButton
          id="verify-invite-code-button"
          class="py-2 px-4 rounded font-semibold text-sm font-button bg-button-guest-primary text-button-label disabled:opacity-60 disabled:cursor-not-allowed"
          @click="verifyInviteCode"
          :disabled="isVerifyingCode"
        >
          {{ isVerifyingCode ? 'Unlocking...' : 'Unlock' }}
        </DebouncedButton>
      </div>
    </SlotModal>
  </div>
</template>

<script lang="ts" setup>
import moment from 'moment'
import { computed, ref, toRefs } from 'vue'

import LockClosedIcon from '@/assets/lock-closed.svg?component'
import TextInputGuest from '@/common/components/molecules/TextInputGuest.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import { StorePerformance } from '@/models'
import DebouncedButton from '@/modules/guestUi/components/atoms/DebouncedButton.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError, clearError, displayHandled } from '@/utils/errors'

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'click', performance: StorePerformance): void
}>()

const props = withDefaults(
  defineProps<{
    error: ErrorData
    performance: StorePerformance
    forcePurchase?: boolean
    buttonClasses?: string
    unlockButtonClasses?: string
  }>(),
  { forcePurchase: false, buttonClasses: '', unlockButtonClasses: '' },
)
const { error, performance, forcePurchase } = toRefs(props)

const checkoutStore = useCheckoutStore()
const inviteCode = ref<string>('')
const displayCodeModal = ref<boolean>(false)
function closeModal() {
  displayCodeModal.value = false
  inviteCode.value = ''
  clearError(errorOnVerifyingCode.value)
}
function openModal() {
  displayCodeModal.value = true
}
const isVerifyingCode = ref<boolean>(false)
const errorOnVerifyingCode = ref<ErrorData>(blankError())
async function verifyInviteCode() {
  if (!inviteCode.value || isVerifyingCode.value) return
  clearError(errorOnVerifyingCode.value)
  try {
    isVerifyingCode.value = true
    await checkoutStore.verifyInviteCode(inviteCode.value)
    if (checkoutStore.isPerformanceUnlockedByInviteCode(performance.value)) {
      displayCodeModal.value = false
    } else {
      errorOnVerifyingCode.value.message = 'The invite code is not valid for this performance'
    }
  } catch (e) {
    displayHandled(e, errorOnVerifyingCode.value)
  } finally {
    isVerifyingCode.value = false
  }
}

const settingStore = useSettingStore()
const timeStr = computed<string>(() => settingStore.timeStr(performance.value.datetime))

const isUnlockedByInviteCode = computed<boolean>(() => {
  return checkoutStore.isPerformanceUnlockedByInviteCode(performance.value)
})

const isOnSale = computed<boolean>(() => {
  if (forcePurchase.value || isUnlockedByInviteCode.value) return true

  if (!performance.value.on_sale_datetime) return true // immediate on sale
  return moment().isAfter(performance.value.on_sale_datetime)
})
const hasPurchaseStop = computed<boolean>(() => {
  // The store gate first: it compares absolute instants, so it can't drift renderer-local the way a
  // date-string comparison did, and it is the same gate `setPerformance` rejects the click with.
  if (!checkoutStore.isPerformanceVisibleOnTheList(performance.value)) return true
  if (!performance.value.off_sale_datetime) return false
  return moment().isAfter(performance.value.off_sale_datetime)
})
const isStartingCheckout = ref<boolean>(false)
const disabled = computed<boolean>(
  () =>
    performance.value.sold || !isOnSale.value || hasPurchaseStop.value || isStartingCheckout.value,
)

async function onClick() {
  clearError(error.value)
  if (performance.value.sold || isStartingCheckout.value) return

  emit('click', performance.value)
  try {
    isStartingCheckout.value = true
    await checkoutStore.setPerformance(performance.value, error.value)
  } finally {
    isStartingCheckout.value = false
  }
}

const buttonText = computed<string>(() => {
  if (isStartingCheckout.value) return 'One moment...'
  return performance.value.button_text || `${timeStr.value} SHOW`
})
</script>
